/**
 * Learning Style Action Generator
 * 
 * Genereert leer-acties aangepast aan de leerstijl van de gebruiker:
 * - Visual: video's, schema's, diagrammen
 * - Auditory: podcasts, gesprekken, uitleg
 * - Reading: artikelen, boeken, documentatie
 * - Kinesthetic: praktijk, oefenen, doen
 */

import { LearningStyle } from '../assessment/testDefinitions';

/**
 * Genereer leer-actie op basis van leerstijl
 * Ondersteunt zowel enkele leerstijl als array van leerstijlen
 */
export function generateLearningAction(
  topic: string,
  learningStyle?: LearningStyle | LearningStyle[]
): string {
  // Als array gegeven, gebruik eerste stijl (of random voor variatie)
  let style: LearningStyle;
  if (Array.isArray(learningStyle)) {
    if (learningStyle.length === 0) {
      style = 'reading'; // Default
    } else {
      // Gebruik eerste stijl, of wissel af op basis van topic voor variatie
      const index = topic.length % learningStyle.length;
      style = learningStyle[index];
    }
  } else {
    style = learningStyle || 'reading';
  }

  const templates: Record<LearningStyle, string[]> = {
    visual: [
      `Bekijk video tutorials over ${topic}`,
      `Bekijk een online cursus met beelden over ${topic}`,
      `Zoek een visuele uitleg (infographic, schema) over ${topic}`,
      `Kijk naar voorbeelden en demo's over ${topic}`,
    ],
    auditory: [
      `Luister naar een podcast over ${topic}`,
      `Volg een webinar met uitleg over ${topic}`,
      `Plan een gesprek met een expert over ${topic}`,
      `Luister naar een audioboek over ${topic}`,
    ],
    reading: [
      `Lees 2-3 artikelen over ${topic}`,
      `Lees een boek of handleiding over ${topic}`,
      `Lees documentatie en case studies over ${topic}`,
      `Lees blog posts van experts over ${topic}`,
    ],
    kinesthetic: [
      `Doe een praktijkoefening met ${topic}`,
      `Experimenteer zelf met ${topic}`,
      `Volg een hands-on workshop over ${topic}`,
      `Start een klein testproject met ${topic}`,
    ],
  };

  const options = templates[style];
  // Rotate through options based on topic length (simple but deterministic)
  const index = topic.length % options.length;
  return options[index];
}

/**
 * Genereer meerdere leer-acties voor verschillende leerstijlen
 * Handig wanneer gebruiker meerdere leerstijlen heeft geselecteerd
 */
export function generateLearningActionsForStyles(
  topic: string,
  learningStyles: LearningStyle[]
): string[] {
  if (learningStyles.length === 0) {
    return [generateLearningAction(topic, 'reading')];
  }
  
  // Genereer één actie per geselecteerde leerstijl
  return learningStyles.map((style) => generateLearningAction(topic, style));
}

/**
 * Genereer meerdere leer-acties voor verschillende leerstijlen
 * (voor als gebruiker geen voorkeur heeft aangegeven)
 */
export function generateMixedLearningActions(topic: string): string[] {
  return [
    generateLearningAction(topic, 'reading'),
    generateLearningAction(topic, 'visual'),
    generateLearningAction(topic, 'auditory'),
    generateLearningAction(topic, 'kinesthetic'),
  ];
}

/**
 * Vertaal generieke "Lees over X" acties naar leerstijl-specifiek
 */
export function adaptActionToLearningStyle(
  action: string,
  learningStyle?: LearningStyle
): string {
  // Als geen leerstijl bekend, houd origineel
  if (!learningStyle) return action;

  // Detecteer "Lees over X" patronen
  const readPatterns = [
    /^Lees\s+(.+)/i,
    /^Lees\s+\d+-?\d*\s+(\w+)\s+over\s+(.+)/i, // "Lees 2-3 artikelen over X"
    /^Volg een online cursus over\s+(.+)/i, // Dit kan ook aangepast worden
  ];

  for (const pattern of readPatterns) {
    const match = action.match(pattern);
    if (match) {
      // Extract topic (laatste match groep)
      const topic = match[match.length - 1];
      return generateLearningAction(topic, learningStyle);
    }
  }

  // Als geen match, return origineel
  return action;
}

/**
 * Vervang "Lees" prefix met leerstijl-specifieke actie
 */
export function replaceLearningVerb(
  originalAction: string,
  learningStyle?: LearningStyle
): string {
  if (!learningStyle) return originalAction;

  const verbs: Record<LearningStyle, string> = {
    visual: 'Bekijk',
    auditory: 'Luister naar',
    reading: 'Lees',
    kinesthetic: 'Oefen met',
  };

  // Replace "Lees" at start with appropriate verb
  if (originalAction.startsWith('Lees ')) {
    return originalAction.replace(/^Lees\s+/i, `${verbs[learningStyle]} `);
  }

  return originalAction;
}

/**
 * Format resource type based on learning style
 */
export function getResourceType(learningStyle?: LearningStyle): string {
  const types: Record<LearningStyle, string> = {
    visual: 'video of cursus',
    auditory: 'podcast of webinar',
    reading: 'artikel of boek',
    kinesthetic: 'workshop of oefening',
  };

  return learningStyle ? types[learningStyle] : 'bron';
}
